import { useState, useEffect, useRef, useMemo, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, GripVertical, Info, Trash2, FileSpreadsheet } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import AsyncItemSelect from '../components/AsyncItemSelect';
import {
  bulkSetStartingValues,
  editItemStartingValue,
  getItemStartingValue,
  setItemStartingValue,
} from '../utils/inventoryStartingValueApi';
import { getAllAccounts } from '../utils/accountApi';
import { getItemById } from '../utils/itemApi';
import { ApiValidationError } from '../types/auth';
import type { Item } from '../types/item';
import type { GlAccount } from '../types/account';
import type {
  BulkStartingValueLine,
  InventoryStartingValue,
} from '../types/inventoryStartingValue';
import { formatCurrency } from '../utils/formatCurrency';
import { btnPrimary } from '../utils/uiTheme';
import { toast, toastApiError } from '../utils/toast';
import {
  BULK_STARTING_VALUE_MAX_LINES,
  type StartingValueLocalLine,
  canEditStartingValue,
  canSetStartingValue,
  canShowStartingValueEdit,
  defaultEquityAccountId,
  emptyStartingValueLine,
  itemHasZeroQty,
  lineTotalValue,
  parseStartingValueNumber,
  todayIso,
} from '../utils/inventoryStartingValueForm';

type EditBaseline = {
  as_of_date: string;
  qty: number;
  unit_cost: number;
  equity_account_id: string;
  memo: string;
};

const toDateInputValue = (value: string) => value.slice(0, 10);

const InventoryStartingValueManage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const singleItemId = Number(searchParams.get('itemId')) || 0;
  const isSingleMode = singleItemId > 0;
  const isEditMode = isSingleMode && searchParams.get('mode') === 'edit';

  const itemLookupRef = useRef<Map<number, Item>>(new Map());
  const editBaselineRef = useRef<EditBaseline | null>(null);

  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [singleItem, setSingleItem] = useState<Item | null>(null);
  const [existingStartingValue, setExistingStartingValue] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});

  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [equityAccountId, setEquityAccountId] = useState('');
  const [batchMemo, setBatchMemo] = useState('');
  const [lines, setLines] = useState<StartingValueLocalLine[]>([emptyStartingValueLine()]);

  const equityAccounts = accounts.filter((a) => a.active && a.account_type === 'EQUITY');

  const estimatedBatchTotal = useMemo(
    () => lines.reduce((sum, line) => sum + lineTotalValue(line), 0),
    [lines]
  );

  const activeLineCount = useMemo(
    () =>
      lines.filter((line) => {
        const qty = parseStartingValueNumber(line.qty);
        const unitCost = parseStartingValueNumber(line.unit_cost);
        return (line.item_id > 0 || line.item_sku.trim()) && qty > 0 && unitCost >= 0;
      }).length,
    [lines]
  );

  const applyEditPrefill = (existing: InventoryStartingValue, accountList: GlAccount[]) => {
    const asOf = toDateInputValue(existing.as_of_date);
    const qty = String(parseStartingValueNumber(existing.qty));
    const unitCost = String(parseStartingValueNumber(existing.unit_cost));
    const memo = existing.memo ?? '';
    const equityId = existing.equity_account
      ? String(existing.equity_account.id)
      : defaultEquityAccountId(accountList);

    setAsOfDate(asOf);
    setEquityAccountId(equityId);
    setBatchMemo(memo);
    setLines([
      {
        item_id: existing.item.id,
        item_name: existing.item.name,
        item_sku: existing.item.sku,
        qty,
        unit_cost: unitCost,
        memo: '',
      },
    ]);

    editBaselineRef.current = {
      as_of_date: asOf,
      qty: parseStartingValueNumber(qty),
      unit_cost: parseStartingValueNumber(unitCost),
      equity_account_id: equityId,
      memo,
    };
  };

  useEffect(() => {
    if (isEditMode) {
      if (!canEditStartingValue()) {
        navigate('/inventory', { replace: true });
        return;
      }
    } else if (!canSetStartingValue()) {
      navigate('/inventory', { replace: true });
      return;
    }

    const load = async () => {
      try {
        const accountList = await getAllAccounts();
        setAccounts(accountList);

        if (isSingleMode) {
          const item = await getItemById(singleItemId);
          setSingleItem(item);
          itemLookupRef.current.set(item.id, item);

          const existing = await getItemStartingValue(singleItemId);
          const hasOpen = existing != null && existing.status === 'OPEN';
          setExistingStartingValue(hasOpen);

          if (isEditMode) {
            if (!hasOpen || !existing || !canShowStartingValueEdit(existing)) {
              toast.error(
                !hasOpen
                  ? 'No open opening stock to edit for this item.'
                  : 'Opening stock cannot be edited (sold stock requires cost correction).'
              );
              navigate(
                hasOpen
                  ? `/inventory-starting-values/${singleItemId}`
                  : '/inventory',
                { replace: true }
              );
              return;
            }
            applyEditPrefill(existing, accountList);
            return;
          }

          setEquityAccountId(defaultEquityAccountId(accountList));
          if (item.item_type === 'INVENTORY') {
            setLines([
              {
                item_id: item.id,
                item_name: item.name,
                item_sku: item.sku,
                qty: '',
                unit_cost: item.standard_cost
                  ? String(parseStartingValueNumber(item.standard_cost))
                  : '',
                memo: '',
              },
            ]);
          }
        } else {
          setEquityAccountId(defaultEquityAccountId(accountList));
        }
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Failed to load form data');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [navigate, isSingleMode, singleItemId, isEditMode]);

  const updateLine = (index: number, patch: Partial<StartingValueLocalLine>) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const addLine = () => {
    if (isSingleMode) return;
    setLines((prev) =>
      prev.length >= BULK_STARTING_VALUE_MAX_LINES ? prev : [...prev, emptyStartingValueLine()]
    );
  };

  const removeLine = (index: number) => {
    if (isSingleMode) return;
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const clearAllLines = () => {
    if (isEditMode && editBaselineRef.current && singleItem) {
      const baseline = editBaselineRef.current;
      setAsOfDate(baseline.as_of_date);
      setEquityAccountId(baseline.equity_account_id);
      setBatchMemo(baseline.memo);
      setLines([
        {
          item_id: singleItem.id,
          item_name: singleItem.name,
          item_sku: singleItem.sku,
          qty: String(baseline.qty),
          unit_cost: String(baseline.unit_cost),
          memo: '',
        },
      ]);
      return;
    }

    if (isSingleMode && singleItem) {
      setLines([
        {
          item_id: singleItem.id,
          item_name: singleItem.name,
          item_sku: singleItem.sku,
          qty: '',
          unit_cost: singleItem.standard_cost
            ? String(parseStartingValueNumber(singleItem.standard_cost))
            : '',
          memo: '',
        },
      ]);
      return;
    }
    setLines([emptyStartingValueLine()]);
  };

  const selectItemForLine = (index: number, item: Item | null) => {
    if (isEditMode) return;

    if (!item) {
      updateLine(index, {
        item_id: 0,
        item_name: '',
        item_sku: '',
      });
      return;
    }

    if (item.item_type !== 'INVENTORY' || !item.active) {
      return;
    }

    itemLookupRef.current.set(item.id, item);
    updateLine(index, {
      item_id: item.id,
      item_name: item.name,
      item_sku: item.sku,
      unit_cost:
        !lines[index]?.unit_cost.trim() && item.standard_cost
          ? String(parseStartingValueNumber(item.standard_cost))
          : lines[index]?.unit_cost ?? '',
    });
  };

  const buildApiLines = (): BulkStartingValueLine[] | null => {
    const apiLines: BulkStartingValueLine[] = [];
    const seenItemIds = new Set<number>();
    const seenSkus = new Set<string>();

    for (const line of lines) {
      const qty = parseStartingValueNumber(line.qty);
      const unitCost = parseStartingValueNumber(line.unit_cost);
      const sku = line.item_sku.trim();

      if ((!line.item_id && !sku) || qty <= 0 || unitCost < 0 || !Number.isFinite(qty) || !Number.isFinite(unitCost)) {
        continue;
      }

      if (line.item_id) {
        if (seenItemIds.has(line.item_id)) {
          setFormError('Each item can only appear once in a batch.');
          return null;
        }
        seenItemIds.add(line.item_id);
        apiLines.push({
          item_id: line.item_id,
          qty,
          unit_cost: unitCost,
          ...(line.memo.trim() ? { memo: line.memo.trim() } : {}),
        });
      } else {
        const skuKey = sku.toLowerCase();
        if (seenSkus.has(skuKey)) {
          setFormError('Each SKU can only appear once in a batch.');
          return null;
        }
        seenSkus.add(skuKey);
        apiLines.push({
          sku,
          qty,
          unit_cost: unitCost,
          ...(line.memo.trim() ? { memo: line.memo.trim() } : {}),
        });
      }
    }

    return apiLines;
  };

  const lineFieldError = (index: number, field: string) =>
    fieldErrors[`lines.${index}.${field}`]?.[0] ||
    fieldErrors[`lines.${index}`]?.[0];

  const hasEditChanges = (line: BulkStartingValueLine) => {
    const baseline = editBaselineRef.current;
    if (!baseline) return true;
    const memo = (line.memo || batchMemo).trim() || '';
    const equityId = equityAccountId || '';
    return (
      asOfDate !== baseline.as_of_date ||
      line.qty !== baseline.qty ||
      line.unit_cost !== baseline.unit_cost ||
      equityId !== baseline.equity_account_id ||
      memo !== baseline.memo
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isEditMode) {
      if (!canEditStartingValue()) return;
    } else if (!canSetStartingValue()) {
      return;
    }

    setFormError(null);
    setFieldErrors({});

    const apiLines = buildApiLines();
    if (!apiLines || apiLines.length === 0) {
      setFormError('Add at least one line with a product or SKU, quantity > 0, and unit cost.');
      return;
    }

    if (!isSingleMode && apiLines.length > BULK_STARTING_VALUE_MAX_LINES) {
      setFormError(`Bulk import supports up to ${BULK_STARTING_VALUE_MAX_LINES} lines per request.`);
      return;
    }

    const equityId = equityAccountId ? Number(equityAccountId) : undefined;

    try {
      setSubmitting(true);

      if (isSingleMode) {
        const line = apiLines[0];
        const payload = {
          as_of_date: asOfDate,
          qty: line.qty,
          unit_cost: line.unit_cost,
          ...(equityId ? { equity_account_id: equityId } : {}),
          memo: (line.memo || batchMemo).trim() || null,
        };

        if (isEditMode) {
          if (!hasEditChanges(line)) {
            setFormError('Change at least one field before saving.');
            return;
          }
          const result = await editItemStartingValue(singleItemId, payload);
          if (result.edit_summary.gl_touched) {
            toast.success(
              `Opening stock updated for ${result.data.item.name}. Inventory and equity journals were refreshed.`
            );
          } else {
            toast.success(`Opening stock updated for ${result.data.item.name}.`);
          }
          navigate(`/inventory-starting-values/${singleItemId}`);
          return;
        }

        const saved = await setItemStartingValue(singleItemId, payload);
        toast.success(`Opening stock set for ${saved.item.name}.`);
        navigate(`/inventory-starting-values/${singleItemId}`);
        return;
      }

      const result = await bulkSetStartingValues({
        as_of_date: asOfDate,
        ...(equityId ? { equity_account_id: equityId } : {}),
        memo: batchMemo.trim() || null,
        lines: apiLines,
      });

      toast.success(
        `${result.meta.count} item${result.meta.count === 1 ? '' : 's'} imported · ${formatCurrency(parseStartingValueNumber(result.meta.total_value))} total value`
      );
      navigate('/inventory-transactions', { state: { typeFilter: 'starting_value' } });
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFormError(err.message);
        setFieldErrors(err.errors);
      } else {
        toastApiError(err, isEditMode ? 'Failed to edit opening stock' : 'Failed to save opening stock');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const singleItemBlocked =
    !isEditMode &&
    isSingleMode &&
    singleItem != null &&
    (singleItem.item_type !== 'INVENTORY' || !itemHasZeroQty(singleItem.qty_on_hand));

  const cancelTo = isEditMode
    ? `/inventory-starting-values/${singleItemId}`
    : '/inventory';

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto p-6 pb-28">
            <div className="mb-6">
              <Link
                to={cancelTo}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                {isEditMode ? 'Back to opening stock' : 'Back to Inventory'}
              </Link>
              <h1 className="text-2xl font-semibold text-gray-900">
                {isEditMode
                  ? 'Edit Opening Stock'
                  : isSingleMode
                    ? 'Set Opening Stock'
                    : 'Bulk Opening Stock Import'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {isEditMode
                  ? 'Update quantity, unit cost, date, equity, or memo while opening stock is unsold'
                  : isSingleMode
                    ? 'Set opening quantity and FIFO unit cost before sales or bill receipts'
                    : `Import up to ${BULK_STARTING_VALUE_MAX_LINES} SKUs per batch — Dr Inventory Asset / Cr Owner Equity`}
              </p>
            </div>

            {formError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                  <p className="text-red-800 text-sm">{formError}</p>
                </div>
              </div>
            )}

            {!isSingleMode && (
              <div className="mb-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg text-sm text-cyan-900 flex flex-wrap items-center justify-between gap-3">
                <span>Importing many SKUs? Use CSV upload with automatic batching.</span>
                <Link
                  to="/inventory-opening-stock-import"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 font-medium text-cyan-900 border border-cyan-300 rounded-lg hover:bg-cyan-100"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Bulk CSV import
                </Link>
              </div>
            )}

            {existingStartingValue && isSingleMode && !isEditMode && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                This item already has an open starting value.{' '}
                <Link
                  to={`/inventory-starting-values/${singleItemId}`}
                  className="font-medium underline"
                >
                  View and void it
                </Link>{' '}
                before setting again.
              </div>
            )}

            {singleItemBlocked && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                {singleItem?.item_type !== 'INVENTORY'
                  ? 'Only INVENTORY items can have opening stock.'
                  : 'Item must have zero quantity on hand and no existing cost layers. Receive bills or void existing stock first.'}
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-8">
              <section className="pb-8 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900 mb-5">Opening details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      As-of date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={asOfDate}
                      onChange={(e) => setAsOfDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    />
                    {fieldErrors.as_of_date && (
                      <p className="text-xs text-red-600 mt-1">{fieldErrors.as_of_date[0]}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {isSingleMode ? 'Memo' : 'Batch memo'}
                    </label>
                    <input
                      type="text"
                      value={batchMemo}
                      onChange={(e) => setBatchMemo(e.target.value)}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    />
                  </div>
                  <div>
                    <label className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 mb-1.5">
                      Equity account
                      <span title="Credit side of the opening entry (defaults to Owner Equity 3000)">
                        <Info className="h-3.5 w-3.5 text-gray-400" />
                      </span>
                    </label>
                    <select
                      value={equityAccountId}
                      onChange={(e) => setEquityAccountId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    >
                      <option value="">Default (Owner Equity 3000)</option>
                      {equityAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.account_number} — {account.name}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.equity_account_id && (
                      <p className="text-xs text-red-600 mt-1">{fieldErrors.equity_account_id[0]}</p>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <h3 className="text-base font-semibold text-gray-900 mb-5">Line items</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full min-w-[960px] text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-8 px-2 py-2" aria-hidden="true" />
                        <th className="w-10 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          #
                        </th>
                        <th className="min-w-[180px] px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Product
                        </th>
                        <th className="w-28 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          SKU
                        </th>
                        <th className="w-28 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Qty
                        </th>
                        <th className="w-32 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Unit cost
                        </th>
                        <th className="w-32 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Line value
                        </th>
                        {!isSingleMode && (
                          <th className="min-w-[120px] px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Line memo
                          </th>
                        )}
                        {!isSingleMode && <th className="w-10 px-2 py-2" aria-hidden="true" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {lines.map((line, index) => {
                        const total = lineTotalValue(line);
                        const lineError = lineFieldError(index, 'qty') || lineFieldError(index, 'unit_cost');

                        return (
                          <tr key={index} className="hover:bg-gray-50/80 align-top">
                            <td className="px-2 py-2 text-gray-300">
                              <GripVertical className="h-4 w-4" />
                            </td>
                            <td className="px-2 py-2 text-gray-500">{index + 1}</td>
                            <td className="px-2 py-2">
                              <AsyncItemSelect
                                className="w-full"
                                value={line.item_id}
                                selectedLabel={line.item_name}
                                onSelect={(item) => selectItemForLine(index, item)}
                                onClear={() => selectItemForLine(index, null)}
                                placeholder="Select product"
                                searchPlaceholder="Search by name or SKU..."
                                itemType="INVENTORY"
                                disabled={isSingleMode}
                              />
                            </td>
                            <td className="px-2 py-2">
                              {isSingleMode ? (
                                <span className="font-mono text-xs text-gray-600">{line.item_sku || '—'}</span>
                              ) : (
                                <input
                                  type="text"
                                  value={line.item_sku}
                                  onChange={(e) =>
                                    updateLine(index, {
                                      item_sku: e.target.value,
                                      ...(e.target.value.trim()
                                        ? { item_id: 0, item_name: '' }
                                        : {}),
                                    })
                                  }
                                  placeholder="Or enter SKU"
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                />
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                required
                                value={line.qty}
                                onChange={(e) => updateLine(index, { qty: e.target.value })}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right font-mono focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                required
                                value={line.unit_cost}
                                onChange={(e) => updateLine(index, { unit_cost: e.target.value })}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right font-mono focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                              />
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-gray-700">
                              {total > 0 ? formatCurrency(total) : '—'}
                            </td>
                            {!isSingleMode && (
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  value={line.memo}
                                  onChange={(e) => updateLine(index, { memo: e.target.value })}
                                  placeholder="Optional"
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                />
                                {lineError && (
                                  <p className="text-xs text-red-600 mt-1">{lineError}</p>
                                )}
                              </td>
                            )}
                            {!isSingleMode && (
                              <td className="px-2 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeLine(index)}
                                  disabled={lines.length <= 1}
                                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                                  title="Remove line"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {fieldErrors.lines && (
                  <p className="text-xs text-red-600 mt-2">{fieldErrors.lines[0]}</p>
                )}

                {!isSingleMode && (
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      type="button"
                      onClick={addLine}
                      disabled={lines.length >= BULK_STARTING_VALUE_MAX_LINES}
                      className="px-4 py-1.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                    >
                      Add lines
                    </button>
                    <button
                      type="button"
                      onClick={clearAllLines}
                      className="px-4 py-1.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                      Clear all lines
                    </button>
                    <span className="text-sm text-gray-500 self-center">
                      {lines.length}/{BULK_STARTING_VALUE_MAX_LINES} lines
                    </span>
                  </div>
                )}
              </section>
            </div>
          </div>

          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-gray-600">
                {activeLineCount > 0 ? (
                  <>
                    <span className="text-gray-500 mr-3">
                      {activeLineCount} line{activeLineCount === 1 ? '' : 's'}
                    </span>
                    <span className="text-lg font-bold text-slate-800">
                      {formatCurrency(estimatedBatchTotal)}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">No valid lines</span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={cancelTo}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={
                    submitting ||
                    activeLineCount === 0 ||
                    Boolean(existingStartingValue && isSingleMode && !isEditMode) ||
                    Boolean(singleItemBlocked)
                  }
                  className={`${btnPrimary} px-6 py-2.5 text-sm font-semibold disabled:opacity-50`}
                >
                  {submitting
                    ? 'Saving…'
                    : isEditMode
                      ? 'Save changes'
                      : isSingleMode
                        ? 'Save opening stock'
                        : `Import ${activeLineCount || ''} item${activeLineCount === 1 ? '' : 's'}`.trim()}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryStartingValueManage;
